export function canonicalize(value) {
    return JSON.stringify(sortJson(value));
}
export async function sha256Base64url(value) {
    const bytes = typeof value === "string" ? new TextEncoder().encode(value) : value;
    const input = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(input).set(bytes);
    const hash = await crypto.subtle.digest("SHA-256", input);
    return encodeBase64url(new Uint8Array(hash));
}
export function encodeBase64url(bytes) {
    let binary = "";
    for (const byte of bytes)
        binary += String.fromCharCode(byte);
    return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}
export function decodeBase64url(value) {
    const normalized = value.replaceAll("-", "+").replaceAll("_", "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const binary = atob(padded);
    return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}
function ownedBytes(bytes) {
    const copy = new Uint8Array(bytes.byteLength);
    copy.set(bytes);
    return copy;
}
async function encryptKeysetWithAesKey(keyset, key) {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(keyset)));
    return { name: "AES-GCM", iv_b64u: encodeBase64url(iv), ciphertext_b64u: encodeBase64url(new Uint8Array(ciphertext)) };
}
async function decryptKeysetWithAesKey(cipher, key) {
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ownedBytes(decodeBase64url(cipher.iv_b64u)) }, key, ownedBytes(decodeBase64url(cipher.ciphertext_b64u)));
    return JSON.parse(new TextDecoder().decode(plaintext));
}
async function derivePassphraseWrappingKey(passphrase, salt, iterations, usages) {
    const passphraseKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
    return crypto.subtle.deriveKey({ name: "PBKDF2", salt: ownedBytes(salt), iterations, hash: "SHA-256" }, passphraseKey, { name: "AES-GCM", length: 256 }, false, usages);
}
async function derivePrfWrappingKey(prfOutput, salt, usages) {
    const input = await crypto.subtle.importKey("raw", ownedBytes(prfOutput), "HKDF", false, ["deriveKey"]);
    return crypto.subtle.deriveKey({ name: "HKDF", hash: "SHA-256", salt: ownedBytes(salt), info: new TextEncoder().encode("envship vault unlock v1") }, input, { name: "AES-GCM", length: 256 }, false, usages);
}
export async function generateEncryptedKeyFile(label, passphrase) {
    if (!passphrase)
        throw new Error("Add a KeyFile passphrase before creating the encrypted backup.");
    const keyset = await generatePlainKeyset(label);
    return { filename: `${label}.envship-keyfile.json`, keyFile: await createEncryptedKeyFileFromKeyset(keyset, passphrase), keyset };
}
export async function createEncryptedKeyFileFromKeyset(keyset, passphrase) {
    if (!passphrase)
        throw new Error("Add a KeyFile passphrase before creating the encrypted backup.");
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const passphraseKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
    const wrappingKey = await crypto.subtle.deriveKey({ name: "PBKDF2", salt, iterations: 250000, hash: "SHA-256" }, passphraseKey, { name: "AES-GCM", length: 256 }, false, ["encrypt"]);
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, wrappingKey, new TextEncoder().encode(JSON.stringify(keyset)));
    return {
        type: "envship.encrypted_keyfile.v1",
        keyset_id: keyset.keyset_id,
        label: keyset.label,
        encryption_key_id: keyset.encryption_key_id,
        signing_key_id: keyset.signing_key_id,
        kdf: { name: "PBKDF2", hash: "SHA-256", iterations: 250000, salt_b64u: encodeBase64url(salt) },
        cipher: { name: "AES-GCM", iv_b64u: encodeBase64url(iv), ciphertext_b64u: encodeBase64url(new Uint8Array(ciphertext)) },
        public_metadata: { encryption_public_jwk: keyset.encryption_public_jwk, signing_public_jwk: keyset.signing_public_jwk },
        created_at: keyset.created_at
    };
}
export async function generatePlainKeyset(label) {
    const [encryptionPair, signingPair] = await Promise.all([
        crypto.subtle.generateKey({ name: "RSA-OAEP", modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]), hash: "SHA-256" }, true, ["encrypt", "decrypt"]),
        crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"])
    ]);
    const [encryptionPrivateJwk, encryptionPublicJwk, signingPrivateJwk, signingPublicJwk] = await Promise.all([
        crypto.subtle.exportKey("jwk", encryptionPair.privateKey),
        crypto.subtle.exportKey("jwk", encryptionPair.publicKey),
        crypto.subtle.exportKey("jwk", signingPair.privateKey),
        crypto.subtle.exportKey("jwk", signingPair.publicKey)
    ]);
    return {
        type: "envship.private_keyset.v1",
        keyset_id: `kst_${crypto.randomUUID()}`,
        label,
        encryption_key_id: `enc_${(await sha256Base64url(canonicalize(encryptionPublicJwk))).slice(0, 18)}`,
        encryption_private_jwk: encryptionPrivateJwk,
        encryption_public_jwk: encryptionPublicJwk,
        signing_key_id: `sig_${(await sha256Base64url(canonicalize(signingPublicJwk))).slice(0, 18)}`,
        signing_private_jwk: signingPrivateJwk,
        signing_public_jwk: signingPublicJwk,
        created_at: new Date().toISOString()
    };
}
export async function createEncryptedKeyBundle(input) {
    if (!input.recoveryPassphrase && !input.prf)
        throw new Error("Add a passkey PRF wrap before creating the encrypted key bundle.");
    const now = new Date().toISOString();
    const wraps = [];
    if (input.recoveryPassphrase) {
        const passphraseSalt = crypto.getRandomValues(new Uint8Array(16));
        const passphraseKey = await derivePassphraseWrappingKey(input.recoveryPassphrase, passphraseSalt, 250000, ["encrypt"]);
        wraps.push({
            type: "passphrase_pbkdf2_aes_gcm",
            kdf: { name: "PBKDF2", hash: "SHA-256", iterations: 250000, salt_b64u: encodeBase64url(passphraseSalt) },
            cipher: await encryptKeysetWithAesKey(input.keyset, passphraseKey),
            created_at: now
        });
    }
    if (input.prf) {
        wraps.unshift(await createPrfWrap(input.keyset, input.prf.credentialIdB64u, input.prf.output, now));
    }
    return {
        type: "envship.encrypted_key_bundle.v1",
        keyset_id: input.keyset.keyset_id,
        label: input.keyset.label,
        encryption_key_id: input.keyset.encryption_key_id,
        signing_key_id: input.keyset.signing_key_id,
        public_metadata: {
            encryption_public_jwk: input.keyset.encryption_public_jwk,
            signing_public_jwk: input.keyset.signing_public_jwk
        },
        wraps,
        created_at: input.keyset.created_at,
        updated_at: now
    };
}
async function createPrfWrap(keyset, credentialIdB64u, output, createdAt = new Date().toISOString()) {
    const prfSalt = crypto.getRandomValues(new Uint8Array(32));
    const prfKey = await derivePrfWrappingKey(output, prfSalt, ["encrypt"]);
    return {
        type: "webauthn_prf_hkdf_aes_gcm",
        credential_id_b64u: credentialIdB64u,
        salt_b64u: encodeBase64url(prfSalt),
        hkdf_info: "envship vault unlock v1",
        cipher: await encryptKeysetWithAesKey(keyset, prfKey),
        created_at: createdAt
    };
}
export async function createPrfOnlyEncryptedKeyBundle(input) {
    return createEncryptedKeyBundle({
        keyset: input.keyset,
        prf: { credentialIdB64u: input.credentialIdB64u, output: input.prfOutput }
    });
}
export async function upsertPrfWrapForKeyBundle(input) {
    const wrap = await createPrfWrap(input.keyset, input.credentialIdB64u, input.prfOutput);
    return {
        ...input.bundle,
        wraps: [wrap, ...input.bundle.wraps.filter((item) => !(item.type === "webauthn_prf_hkdf_aes_gcm" && item.credential_id_b64u === input.credentialIdB64u))],
        updated_at: new Date().toISOString()
    };
}
export async function unlockKeyBundleWithPassphrase(bundle, passphrase) {
    const wrap = bundle.wraps.find((item) => item.type === "passphrase_pbkdf2_aes_gcm");
    if (!wrap || wrap.type !== "passphrase_pbkdf2_aes_gcm")
        throw new Error("This encrypted key bundle does not have a recovery passphrase wrap.");
    const key = await derivePassphraseWrappingKey(passphrase, decodeBase64url(wrap.kdf.salt_b64u), wrap.kdf.iterations, ["decrypt"]);
    return decryptKeysetWithAesKey(wrap.cipher, key);
}
export async function unlockKeyBundleWithPrf(bundle, credentialIdB64u, prfOutput) {
    const wrap = bundle.wraps.find((item) => item.type === "webauthn_prf_hkdf_aes_gcm" && item.credential_id_b64u === credentialIdB64u);
    if (!wrap || wrap.type !== "webauthn_prf_hkdf_aes_gcm")
        throw new Error("This encrypted key bundle does not have a matching passkey PRF wrap.");
    const key = await derivePrfWrappingKey(prfOutput, decodeBase64url(wrap.salt_b64u), ["decrypt"]);
    return decryptKeysetWithAesKey(wrap.cipher, key);
}
export async function unlockKeyFile(keyFile, passphrase) {
    const passphraseKey = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
    const wrappingKey = await crypto.subtle.deriveKey({ name: "PBKDF2", salt: ownedBytes(decodeBase64url(keyFile.kdf.salt_b64u)), iterations: keyFile.kdf.iterations, hash: keyFile.kdf.hash }, passphraseKey, { name: "AES-GCM", length: 256 }, false, ["decrypt"]);
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ownedBytes(decodeBase64url(keyFile.cipher.iv_b64u)) }, wrappingKey, ownedBytes(decodeBase64url(keyFile.cipher.ciphertext_b64u)));
    return JSON.parse(new TextDecoder().decode(plaintext));
}
export async function encryptEnvBundleForRecipients(env, recipients, channelRef) {
    if (recipients.length === 0)
        throw new Error("No active recipients are available for this channel.");
    const dataKey = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
    const exportedDataKey = await crypto.subtle.exportKey("raw", dataKey);
    const wrappedRecipients = await Promise.all(recipients.map(async (recipient) => {
        const recipientPublicKey = await crypto.subtle.importKey("jwk", recipient.encryption_public_jwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["encrypt"]);
        const wrappedKey = await crypto.subtle.encrypt({ name: "RSA-OAEP" }, recipientPublicKey, exportedDataKey);
        return {
            encryption_key_id: recipient.encryption_key_id,
            wrapped_key_b64u: encodeBase64url(new Uint8Array(wrappedKey))
        };
    }));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const plaintext = new TextEncoder().encode(JSON.stringify({ type: "envship.env_map.v1", env }));
    const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, dataKey, plaintext);
    return {
        type: "envship.encrypted_env_bundle.v1",
        algorithm: {
            content: "AES-GCM-256",
            recipient_wrap: "RSA-OAEP-SHA-256"
        },
        recipients: wrappedRecipients,
        cipher: {
            iv_b64u: encodeBase64url(iv),
            ciphertext_b64u: encodeBase64url(new Uint8Array(ciphertext))
        },
        aad: {
            channel_ref: channelRef,
            created_at: new Date().toISOString()
        }
    };
}
export async function encryptEnvBundle(env, keyset, channelRef) {
    return encryptEnvBundleForRecipients(env, [{
            encryption_key_id: keyset.encryption_key_id,
            encryption_public_jwk: keyset.encryption_public_jwk
        }], channelRef);
}
export async function decryptEnvBundle(bundle, keyset) {
    if (bundle.type !== "envship.encrypted_env_bundle.v1")
        throw new Error("Unsupported EnvShip bundle format.");
    const recipient = bundle.recipients.find((item) => item.encryption_key_id === keyset.encryption_key_id);
    if (!recipient)
        throw new Error("This KeyFile is not included in the encrypted bundle recipients.");
    const recipientPrivateKey = await crypto.subtle.importKey("jwk", keyset.encryption_private_jwk, { name: "RSA-OAEP", hash: "SHA-256" }, false, ["decrypt"]);
    const rawDataKey = await crypto.subtle.decrypt({ name: "RSA-OAEP" }, recipientPrivateKey, ownedBytes(decodeBase64url(recipient.wrapped_key_b64u)));
    const dataKey = await crypto.subtle.importKey("raw", rawDataKey, { name: "AES-GCM" }, false, ["decrypt"]);
    const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: ownedBytes(decodeBase64url(bundle.cipher.iv_b64u)) }, dataKey, ownedBytes(decodeBase64url(bundle.cipher.ciphertext_b64u)));
    const decoded = JSON.parse(new TextDecoder().decode(plaintext));
    if (decoded.type !== "envship.env_map.v1" || !decoded.env || typeof decoded.env !== "object") {
        throw new Error("Encrypted bundle decrypted, but its plaintext format was invalid.");
    }
    return decoded.env;
}
export async function signManifest(manifest, keyset) {
    const signingKey = await crypto.subtle.importKey("jwk", keyset.signing_private_jwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["sign"]);
    const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, signingKey, new TextEncoder().encode(canonicalize(manifest)));
    return {
        ...manifest,
        signature: {
            algorithm: "ECDSA-P256-SHA-256",
            signature_b64u: encodeBase64url(new Uint8Array(signature))
        }
    };
}
export async function verifyManifestSignature(manifest, signingPublicJwk) {
    if (!manifest.signature)
        return false;
    const { signature, ...unsigned } = manifest;
    const signingKey = await crypto.subtle.importKey("jwk", signingPublicJwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
    return crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, signingKey, ownedBytes(decodeBase64url(signature.signature_b64u)), new TextEncoder().encode(canonicalize(unsigned)));
}
export async function generateMachineSigningKey(label) {
    const signingPair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]);
    const [privateJwk, publicJwk] = await Promise.all([
        crypto.subtle.exportKey("jwk", signingPair.privateKey),
        crypto.subtle.exportKey("jwk", signingPair.publicKey)
    ]);
    return {
        signing_key_id: `msig_${(await sha256Base64url(canonicalize(publicJwk))).slice(0, 18)}`,
        signing_private_jwk: privateJwk,
        signing_public_jwk: publicJwk,
        label,
        created_at: new Date().toISOString()
    };
}
export async function signMachineRequest(input, signingPrivateJwk) {
    const signingKey = await crypto.subtle.importKey("jwk", signingPrivateJwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["sign"]);
    const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, signingKey, new TextEncoder().encode(canonicalize(input)));
    return encodeBase64url(new Uint8Array(signature));
}
export async function verifyMachineRequest(input, signatureB64u, signingPublicJwk) {
    const signingKey = await crypto.subtle.importKey("jwk", signingPublicJwk, { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"]);
    return crypto.subtle.verify({ name: "ECDSA", hash: "SHA-256" }, signingKey, ownedBytes(decodeBase64url(signatureB64u)), new TextEncoder().encode(canonicalize(input)));
}
export function parseDotenv(input) {
    const env = {};
    for (const rawLine of input.split(/\r?\n/u)) {
        const line = rawLine.trim();
        if (!line || line.startsWith("#"))
            continue;
        const match = /^([A-Za-z_][A-Za-z0-9_]*)=(.*)$/u.exec(line);
        if (!match)
            throw new Error(`Invalid dotenv line: ${rawLine}`);
        let value = match[2] ?? "";
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
            value = value.slice(1, -1);
        }
        env[match[1]] = value;
    }
    return env;
}
export function stringifyDotenv(env) {
    return Object.entries(env)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => `${key}=${/[\s#"']/u.test(value) ? JSON.stringify(value) : value}`)
        .join("\n") + "\n";
}
function sortJson(value) {
    if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        return value;
    }
    if (Array.isArray(value))
        return value.map(sortJson);
    if (typeof value === "object") {
        return Object.fromEntries(Object.entries(value)
            .filter(([, item]) => item !== undefined)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, item]) => [key, sortJson(item)]));
    }
    throw new TypeError(`Unsupported canonical JSON value: ${typeof value}`);
}
//# sourceMappingURL=index.js.map